import { useCallback, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ImageLogo } from '~/assets/images';
import { Button, Input, InputMask } from '~/components';
import { useAuth } from '~/hooks/auth';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';
import { regex_cpf, regex_remove_all_non_digits } from '~/utils/regex';

import {
  Container,
  Wrapper,
  Title,
  Subtitle,
  ConditionsText,
  // SignInText,
  Slogan,
} from './styles';

interface SignUpFormData {
  name: string;
  cpf: string;
  email: string;
  password: string;
  cellphone: string;
}

const SignUp = () => {
  // const { signUpProfessional } = useAuth();
  const { push } = useHistory();
  const { t } = useTranslation(['Yup', 'Toast', 'Professional:SignUp']);

  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);

  const { storageUserData, userData } = useAuth();

  const handleSubmit = useCallback(
    async (data: SignUpFormData) => {
      try {
        setLoading(true);

        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required(t('Yup:name_required')),
          cpf: Yup.string()
            .required(t('Yup:cpf_required'))
            .matches(regex_cpf, {
              excludeEmptyString: true,
              message: t('Yup:cpf_incomplete'),
            }),
          email: Yup.string()
            .required(t('Yup:email_required'))
            .email(t('Yup:email_type')),
          password: Yup.string().required(t('Yup:password_required')),
          confirm_password: Yup.string()
            .required(t('Yup:confirm_password_required'))
            .when('password', {
              is: (value) => !!value.length,
              then: Yup.string().required(t('Yup:confirm_password_required')),
              otherwise: Yup.string(),
            })
            .oneOf([Yup.ref('password')], t('Yup:confirm_password_incorrect')),
          cellphone: Yup.string().required(t('Yup:cellphone_required')),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { name, cpf, email, password, cellphone } = data;

        const cpf_only_numbers = cpf.replace(regex_remove_all_non_digits, '');

        const cellphone_formatted = cellphone.replace(
          regex_remove_all_non_digits,
          '',
        );

        const response = await api.post('/auth/signup/professional', {
          name,
          cpf: cpf_only_numbers,
          email,
          password,
          cellphone: cellphone_formatted,
          role: 'professional',
        });

        const userInfo = {
          ...response.data,
          passwordToSignIn: password,
        };

        storageUserData(userInfo);

        push('/professional/sign-up/complete', userData);
        // push('/professional/download-app');
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        }

        toast.error(t('Toast:error'));
      } finally {
        setLoading(false);
      }
    },
    [push, storageUserData, t, userData],
  );

  return (
    <Container>
      <Wrapper>
        <ImageLogo />

        <Title>{t('Professional:SignUp.title')}</Title>
        <Subtitle>{t('Professional:SignUp.subtitle')}</Subtitle>

        <Form ref={formRef} onSubmit={handleSubmit}>
          <Input
            name="name"
            placeholder={t('Professional:SignUp.name_placeholder')}
          />
          <InputMask
            name="cpf"
            placeholder={t('Professional:SignUp.cpf_placeholder')}
            mask="999.999.999-99"
          />
          <Input
            name="email"
            placeholder={t('Professional:SignUp.email_placeholder')}
          />
          <InputMask
            name="cellphone"
            placeholder={t('Professional:SignUp.cellphone_placeholder')}
            mask="(99) 99999-9999"
          />
          <Input
            name="password"
            placeholder={t('Professional:SignUp.password_placeholder')}
            showIconPassword
          />
          <Input
            name="confirm_password"
            placeholder={t('Professional:SignUp.confirm_password_placeholder')}
            showIconPassword
          />
        </Form>

        <ConditionsText>
          {t('Professional:SignUp.conditions')}{' '}
          <Link to="/terms-of-use">
            {t('Professional:SignUp.terms_of_use')}
          </Link>{' '}
          {t('Professional:SignUp.and')}{' '}
          <Link to="/policy-privacy">
            {t('Professional:SignUp.policy_privacy')}
          </Link>
        </ConditionsText>

        <Button
          onClick={() => formRef.current?.submitForm()}
          style={{ maxWidth: 387 }}
          loading={loading}
        >
          {t('Professional:SignUp.continue')}
        </Button>

        {/* <SignInText>
          {t('Professional:SignUp.sign_in_account')}{' '}
          <Link to="/professional/sign-in">
            {t('Professional:SignUp.sign_in_login')}
          </Link>
        </SignInText> */}

        <Slogan>
          <p>{t('Professional:SignUp.slogan_your_business')}</p>
          <p>{t('Professional:SignUp.slogan_your_hand')}</p>
        </Slogan>
      </Wrapper>
    </Container>
  );
};

export default SignUp;
